import React, { useState, useEffect, useRef } from 'react';

// Global debug log store
window.__DEBUG_LOGS = [];
window.__DEBUG_LOG = (action, data) => {
  const entry = {
    timestamp: Date.now(),
    time: new Date().toLocaleTimeString(),
    action,
    ...data,
    viewport: {
      innerHeight: window.innerHeight,
      innerWidth: window.innerWidth,
      visualHeight: window.visualViewport?.height,
      visualWidth: window.visualViewport?.width,
      scale: window.visualViewport?.scale,
      offsetTop: window.visualViewport?.offsetTop,
    }
  };
  
  window.__DEBUG_LOGS.push(entry);
  if (window.__DEBUG_LOGS.length > 50) {
    window.__DEBUG_LOGS.shift();
  }
  
  // Trigger update for any listening components
  window.dispatchEvent(new CustomEvent('debug-log-update'));
  
  // Also log to regular console for backup
  console.log(`[DEBUG ${entry.time}] ${action}:`, data);
};

export default function MobileConsole() {
  const [isVisible, setIsVisible] = useState(false);
  const [logs, setLogs] = useState([]);
  const [viewportInfo, setViewportInfo] = useState({});
  const scrollRef = useRef(null);
  
  useEffect(() => {
    // Update logs when new entries are added
    const updateLogs = () => {
      setLogs([...window.__DEBUG_LOGS]);
    };
    
    // Update viewport info
    const updateViewport = () => {
      setViewportInfo({
        innerH: window.innerHeight,
        innerW: window.innerWidth,
        visualH: window.visualViewport?.height || 'N/A',
        visualW: window.visualViewport?.width || 'N/A',
        scale: window.visualViewport?.scale || 'N/A',
      });
    };
    
    updateLogs();
    updateViewport();
    
    window.addEventListener('debug-log-update', updateLogs);
    window.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);
    
    const interval = setInterval(updateViewport, 500);
    
    return () => {
      window.removeEventListener('debug-log-update', updateLogs);
      window.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
      clearInterval(interval);
    };
  }, []);
  
  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (scrollRef.current && isVisible) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, isVisible]);
  
  const toggleButtonStyle = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: isVisible ? '#ff4444' : '#4CAF50',
    color: 'white',
    border: 'none',
    fontSize: '24px',
    fontWeight: 'bold',
    cursor: 'pointer',
    zIndex: 2147483647,
    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
  };
  
  const consoleStyle = {
    position: 'fixed',
    top: '10px',
    left: '10px',
    right: '10px',
    bottom: '80px',
    background: 'rgba(0, 0, 0, 0.95)',
    color: '#0f0',
    fontFamily: 'monospace',
    fontSize: '11px',
    padding: '10px',
    borderRadius: '5px',
    border: '1px solid #0f0',
    zIndex: 2147483646,
    display: isVisible ? 'flex' : 'none',
    flexDirection: 'column',
    pointerEvents: 'auto',
  };
  
  const headerStyle = {
    background: 'rgba(0, 255, 0, 0.1)',
    padding: '5px',
    marginBottom: '5px',
    borderRadius: '3px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
  
  const logsStyle = {
    flex: 1,
    overflow: 'auto',
    border: '1px solid #0f0',
    padding: '5px',
    background: 'rgba(0, 0, 0, 0.5)',
  };
  
  const logEntryStyle = {
    marginBottom: '5px',
    padding: '3px',
    borderLeft: '2px solid #0f0',
    paddingLeft: '5px',
    wordBreak: 'break-all',
  };
  
  const clearLogs = () => {
    window.__DEBUG_LOGS = [];
    setLogs([]);
  };
  
  return (
    <>
      <button 
        style={toggleButtonStyle}
        onClick={() => setIsVisible(!isVisible)}
        aria-label="Toggle Debug Console"
      >
        {isVisible ? '✕' : '🐛'}
      </button>
      
      {isVisible && (
        <div style={consoleStyle}>
          <div style={headerStyle}>
            <div>
              <strong>Debug Console</strong>
              {' | '}
              <span style={{ color: '#ff0' }}>
                H: {viewportInfo.innerH}/{viewportInfo.visualH}
                {' | '}
                W: {viewportInfo.innerW}/{viewportInfo.visualW}
                {' | '}
                S: {viewportInfo.scale}
              </span>
            </div>
            <button 
              onClick={clearLogs}
              style={{
                background: '#ff4444',
                color: 'white',
                border: 'none',
                padding: '2px 8px',
                borderRadius: '3px',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
          
          <div style={logsStyle} ref={scrollRef}>
            {logs.length === 0 ? (
              <div style={{ color: '#666' }}>No debug logs yet...</div>
            ) : (
              logs.map((log, index) => (
                <div key={index} style={logEntryStyle}>
                  <div style={{ color: '#0ff' }}>
                    [{log.time}] {log.action}
                  </div>
                  {log.message && (
                    <div style={{ color: '#fff' }}>{log.message}</div>
                  )}
                  {log.measurements && (
                    <div style={{ color: '#ff0' }}>
                      📏 W:{Math.round(log.measurements.width || 0)} 
                      H:{Math.round(log.measurements.height || 0)}
                      {log.measurements.top !== undefined && ` T:${Math.round(log.measurements.top)}`}
                    </div>
                  )}
                  {log.playerSize && (
                    <div style={{ color: '#f0f' }}>
                      🎬 Player: {log.playerSize.width}x{log.playerSize.height}
                    </div>
                  )}
                  {log.styles && (
                    <div style={{ color: '#888', fontSize: '10px' }}>
                      Styles: {log.styles.substring(0, 100)}...
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}